import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
} from "react-router-dom";
import { BrowserRouter as Router } from "react-router-dom";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import ClassFee from "./ClassFee";

const MONTH_SHORT = {
  1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
};

const CARD_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`;

function parseMonthKey(monthKey) {
  const parts = String(monthKey || "").trim().split("-");
  if (parts.length < 2) {
    return { monthLabel: monthKey || "—", year: "", monthNum: "", shortKey: monthKey || "—" };
  }
  const year = parts[0];
  const monthNum = String(parseInt(parts[1], 10)).padStart(2, "0");
  const month = parseInt(parts[1], 10);
  return {
    monthLabel: MONTH_SHORT[month] || monthNum,
    year,
    monthNum,
    shortKey: `${year}-${monthNum}`,
  };
}

function SectionPanel({ icon, title, subtitle, badge, headerCenter, accent = "primary", children }) {
  const accents = {
    primary: {
      headerBg: `linear-gradient(135deg, ${COLORS.primary_color_shade0}, ${COLORS.white})`,
      border: COLORS.primary_color_shade2,
      iconBg: COLORS.primary_color_shade7,
      badgeBg: COLORS.primary_color_shade0,
      badgeColor: COLORS.primary_color_shade8,
    },
    green: {
      headerBg: `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.white})`,
      border: COLORS.green_dark,
      iconBg: COLORS.card_green,
      badgeBg: COLORS.green_light,
      badgeColor: COLORS.leaf_green,
    },
  };
  const tone = accents[accent] || accents.primary;

  return (
    <div
      style={{
        background: COLORS.white,
        borderRadius: "12px",
        border: `1px solid ${tone.border}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          background: tone.headerBg,
          borderBottom: `1px solid ${COLORS.grey2}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: tone.iconBg,
              color: COLORS.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "16px",
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          >
            <i className={`fa ${icon}`} aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: "11px",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: COLORS.black7,
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: COLORS.black6,
                  marginTop: "2px",
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {headerCenter && (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              padding: "0 8px",
            }}
          >
            {headerCenter}
          </div>
        )}

        {badge && (
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              padding: "5px 12px",
              borderRadius: "999px",
              background: tone.badgeBg,
              color: tone.badgeColor,
              border: `1px solid ${tone.border}`,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <div style={{ padding: "16px" }}>{children}</div>
    </div>
  );
}

function SelectedFeeHeader({ fee, modeLabel, feeValue, readmFeeValue, showReadm }) {
  if (!fee) {
    return (
      <span style={{ fontSize: "12px", fontWeight: 600, color: COLORS.black6 }}>
        No fee selected
      </span>
    );
  }

  return (
    <div style={{ textAlign: "center", minWidth: 0, maxWidth: "100%" }}>
      <div
        style={{
          fontSize: "16px",
          fontWeight: 800,
          color: COLORS.primary_color_shade9,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {fee.fee_name}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          marginTop: "6px",
          justifyContent: "center",
        }}
      >
        {modeLabel && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: "999px",
              background: COLORS.primary_color_shade0,
              color: COLORS.primary_color_shade8,
              border: `1px solid ${COLORS.primary_color_shade2}`,
            }}
          >
            {modeLabel}
          </span>
        )}
        {String(fee.is_special_fee) === "1" && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: "999px",
              background: COLORS.amber1,
              color: COLORS.amber9,
            }}
          >
            Special Fee
          </span>
        )}
        {feeValue && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: "999px",
              background: COLORS.green_light,
              color: COLORS.leaf_green,
              border: `1px solid ${COLORS.green_dark}`,
            }}
          >
            {showReadm ? "New: " : ""}₹{feeValue}
          </span>
        )}
        {showReadm && readmFeeValue && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: "999px",
              background: COLORS.green_light,
              color: COLORS.leaf_green,
              border: `1px solid ${COLORS.card_green}`,
            }}
          >
            Re-Adm: ₹{readmFeeValue}
          </span>
        )}
      </div>
    </div>
  );
}

function StyledSelect({ label, icon, children, selectRef, onChange, hint }) {
  return (
    <div>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: COLORS.black7,
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <i
          className={`fa ${icon}`}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: COLORS.primary_color_shade6,
            fontSize: "14px",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <select
          ref={selectRef}
          onChange={onChange}
          style={{
            width: "100%",
            padding: "11px 36px 11px 38px",
            borderRadius: "10px",
            border: `2px solid ${COLORS.primary_color_shade1}`,
            fontSize: "14px",
            fontWeight: 600,
            color: COLORS.black8,
            background: COLORS.white,
            appearance: "none",
            WebkitAppearance: "none",
            cursor: "pointer",
            boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          {children}
        </select>
        <i
          className="fa fa-chevron-down"
          aria-hidden="true"
          style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: COLORS.black6,
            fontSize: "11px",
            pointerEvents: "none",
          }}
        />
      </div>
      {hint && (
        <div style={{ fontSize: "11px", color: COLORS.black6, marginTop: "6px" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function MonthPickerChip({ monthKey, selected, onToggle }) {
  const { monthLabel, shortKey } = parseMonthKey(monthKey);

  const borderColor = selected ? COLORS.primary_color_shade3 : COLORS.primary_color_shade1;
  const headerBg = selected
    ? COLORS.primary_color_shade0
    : `linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.primary_color_shade0} 100%)`;
  const cardShadow = selected
    ? "0 3px 10px rgba(2, 136, 209, 0.18)"
    : "0 2px 8px rgba(2, 136, 209, 0.1)";
  const monthColor = selected ? COLORS.primary_color_shade9 : COLORS.primary_color_shade8;
  const hoverShadow = selected
    ? "0 4px 14px rgba(2, 136, 209, 0.22)"
    : "0 4px 14px rgba(2, 136, 209, 0.16)";

  return (
    <button
      type="button"
      title={monthKey}
      onClick={() => onToggle(monthKey)}
      style={{
        borderRadius: "10px",
        overflow: "hidden",
        background: COLORS.white,
        border: `2px solid ${borderColor}`,
        boxShadow: cardShadow,
        cursor: "pointer",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
        width: "100%",
        padding: 0,
        textAlign: "left",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = hoverShadow;
        if (!selected) {
          e.currentTarget.style.borderColor = COLORS.primary_color_shade3;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = cardShadow;
        if (!selected) {
          e.currentTarget.style.borderColor = borderColor;
        }
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          padding: "8px 10px",
          background: headerBg,
          borderBottom: `1px solid ${selected ? borderColor : COLORS.primary_color_shade1}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
          <span
            style={{
              fontSize: "15px",
              fontWeight: 800,
              color: monthColor,
              lineHeight: 1,
              letterSpacing: "0.02em",
            }}
          >
            {monthLabel}
          </span>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 600,
              color: selected ? COLORS.black6 : COLORS.primary_color_shade6,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.1,
            }}
          >
            {shortKey}
          </span>
        </div>
        <span
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "10px",
            background: selected ? COLORS.green_light : COLORS.white,
            color: selected ? COLORS.leaf_green : COLORS.primary_color_shade7,
            border: `1.5px solid ${selected ? COLORS.green_dark : COLORS.primary_color_shade3}`,
            boxShadow: selected ? "none" : "0 1px 3px rgba(2, 136, 209, 0.12)",
          }}
        >
          <i
            className={`fa ${selected ? "fa-check" : "fa-plus"}`}
            aria-hidden="true"
          />
        </span>
      </div>
      <div
        style={{
          padding: "8px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: selected ? COLORS.white : COLORS.grey0,
        }}
      >
        <span
          style={{
            fontSize: "10px",
            fontWeight: 800,
            padding: "4px 10px",
            borderRadius: "999px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            width: "100%",
            textAlign: "center",
            ...(selected
              ? {
                  background: `linear-gradient(135deg, ${COLORS.green_light}, ${COLORS.green0})`,
                  color: COLORS.leaf_green,
                  border: `1px solid ${COLORS.green_dark}`,
                }
              : {
                  background: COLORS.white,
                  color: COLORS.primary_color_shade7,
                  border: `1px dashed ${COLORS.primary_color_shade2}`,
                }),
          }}
        >
          {selected ? "Included" : "Add month"}
        </span>
      </div>
    </button>
  );
}

const AddClassFee = () => {
  const slt_feename = useRef(null);
  const slt_receipt_mode = useRef(null);
  const history = useHistory();
  const { selected_class_pos } = useParams();

  const [loggedIn, setLoggedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showInputReAdm, setShowInputReAdm] = useState(false);
  const [SESSION_ID, setSessionId] = useState("");
  const [CLASS_ID, setClassId] = useState("");
  const [SECTION_ID, setSectionId] = useState("");
  const [SELECTED_CLASS_POSITION, setSelectedClassPosition] = useState("");
  const [FEE_VALUE, setFeeValue] = useState("");
  const [FEE_VALUE_READMISSION, setFeeValueReadmission] = useState("");
  const [payment_count, setPaymentCount] = useState("");
  const [mFeeName, setFeeName] = useState([]);
  const [mFeeReceiptMode, setFeeReceiptMode] = useState([]);
  const [mClassFee, setClassFee] = useState([]);
  const [mFeeFiltered, setFeeFiltered] = useState([]);
  const [mCurrentSession, setCurrentSession] = useState([]);
  const [mSessionMonth, setSessionMonth] = useState([]);
  const [mApplicableMonth, setApplicableMonth] = useState([]);
  const [selectedFee, setSelectedFee] = useState(null);
  const [selectedModeLabel, setSelectedModeLabel] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const session = SessionManager.getCurrentSession();
    if (session && session.length) {
      setSessionId(session[0].session_id);
      setCurrentSession(session);
    }
  }, []);

  useEffect(() => {
    if (mFeeName.length > 0 && CLASS_ID && SESSION_ID) {
      getClassFee();
    }
  }, [mFeeName, CLASS_ID, SESSION_ID]);

  const onFeeValueChange = (e) => setFeeValue(e.target.value);
  const onFeeValueReAdmChange = (e) => setFeeValueReadmission(e.target.value);

  const onReceiptModeChange = () => {
    const option = slt_receipt_mode.current.selectedOptions[0];
    const position = option.getAttribute("position");
    setSelectedModeLabel(option.value !== "Select Mode" ? option.value : "");
    let session_start_date = mCurrentSession[0]?.start_date || "";

    let count = 0;
    switch (Number(position)) {
      case 0:
        count = 12;
        break;
      case 1:
        count = 4;
        break;
      case 2:
        count = 2;
        break;
      case 3:
        count = 1;
        break;
      case 4:
        count = 3;
        break;
      case 5:
        count = 6;
        break;
    }
    setPaymentCount(count);
    setApplicableMonth([]);
    getAllSessionMonth(session_start_date);
  };

  const onFeeNameChange = () => {
    const option = slt_feename.current.selectedOptions[0];
    const is_special_fee = option.getAttribute("is_special_fee");
    const position = option.getAttribute("position");
    setShowInputReAdm(is_special_fee === "1");
    if (position !== null && position !== "") {
      setSelectedFee(mFeeFiltered[Number(position)] || null);
    } else {
      setSelectedFee(null);
    }
  };

  const getAllSessionMonth = (startDate) => {
    const temp = startDate.split("-");
    const dateObj = new Date(
      Number(temp[0]),
      Number(temp[1]) - 1,
      Number(temp[2]) + 1
    );
    const months = [];
    for (let i = 1; i <= 12; i++) {
      if (i !== 1) dateObj.setMonth(dateObj.getMonth() + 1);
      const MONTH = dateObj.getUTCMonth() + 1;
      const YEAR = dateObj.getUTCFullYear();
      months.push(`${YEAR}-${MONTH < 10 ? "0" + MONTH : MONTH}`);
    }
    setSessionMonth(months);
  };

  const CallBackClassSelect = (
    class_id,
    section_id,
    session_id,
    selected_position
  ) => {
    setClassId(class_id);
    setSectionId(section_id);
    setSessionId(session_id);
    setSelectedClassPosition(selected_position);
    setApplicableMonth([]);
    setSelectedFee(null);
    setSelectedModeLabel("");
    getApplicableFeeAndMode();
  };

  const getApplicableFeeAndMode = () => {
    setLoading(true);
    const FUNCTION_NAME = "getApplicableFeeAndMode.php?";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
    const params = { school_code: SessionManager.getSchoolCode() };

    axios
      .get(URL, { params })
      .then((res) => {
        setLoading(false);
        const response = res.data;
        if (response.api_status === "OK") {
          setFeeReceiptMode(response.data.mode);
          setFeeName(response.data.applicable_fee);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  const getClassFee = () => {
    setLoading(true);
    const FUNCTION_NAME = "getClassFee.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
    const params = {
      session_id: SESSION_ID,
      class_id: CLASS_ID,
      school_code: SessionManager.getSchoolCode(),
    };

    axios
      .get(URL, { params })
      .then((res) => {
        setLoading(false);
        const response = res.data;
        if (response.api_status === "OK") {
          setClassFee(response.data);
          removeAlreadyAddedFee(response.data);
        } else {
          setClassFee([]);
          removeAlreadyAddedFee([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        setClassFee([]);
        removeAlreadyAddedFee([]);
      });
  };

  const removeAlreadyAddedFee = (classFees) => {
    if (!classFees || classFees.length === 0) {
      setFeeFiltered(mFeeName);
      return;
    }

    const filtered = mFeeName.filter(
      (fee) => !classFees.some((cf) => cf.fee_id === fee.fee_id)
    );

    setFeeFiltered(filtered);
  };

  const isMonthApplicable = (month) => mApplicableMonth.includes(month);

  const updateApplicableMonth = (month) => {
    if (isMonthApplicable(month)) {
      setApplicableMonth((prev) => prev.filter((m) => m !== month));
    } else {
      setApplicableMonth((prev) => [...prev, month]);
    }
  };

  const monthSelectionHint = () => {
    if (!payment_count) return "Choose a receipt mode first";
    if (payment_count >= 12) {
      return `Pick at least 10 months · ${mApplicableMonth.length} selected`;
    }
    return `Pick ${payment_count} month${payment_count > 1 ? "s" : ""} · ${mApplicableMonth.length}/${payment_count}`;
  };

  const monthSelectionProgress = () => {
    if (!payment_count) return 0;
    if (payment_count >= 12) {
      return Math.min(100, Math.round((mApplicableMonth.length / 10) * 100));
    }
    return Math.min(100, Math.round((mApplicableMonth.length / payment_count) * 100));
  };

  const monthSelectionComplete = () => {
    if (!payment_count) return false;
    if (payment_count >= 12) return mApplicableMonth.length >= 10;
    return mApplicableMonth.length === payment_count;
  };

  const isValidInput = () => {
    if (!CLASS_ID || !SESSION_ID) {
      myToast.notify_warning("Select a class and session");
      return false;
    }
    if (!slt_feename.current?.selectedOptions[0]?.getAttribute("position")) {
      myToast.notify_warning("Select Fee");
      return false;
    }
    if (!slt_receipt_mode.current?.selectedOptions[0]?.getAttribute("position")) {
      myToast.notify_warning("Select Fee Receipt Mode");
      return false;
    }
    if (!FEE_VALUE) {
      myToast.notify_warning("Enter Fee Value");
      return false;
    }
    if (showInputReAdm && !FEE_VALUE_READMISSION) {
      myToast.notify_warning("Enter Fee Value for Re-Admission");
      return false;
    }
    if (payment_count < 12 && payment_count !== mApplicableMonth.length) {
      myToast.notify_warning(`Select ${payment_count} Collection Months`);
      return false;
    }
    if (payment_count >= 12 && mApplicableMonth.length < 10) {
      myToast.notify_warning("Select at least 10 Collection Months");
      return false;
    }
    return true;
  };

  const submitClassFee = () => {
    if (!isValidInput()) return;
    setLoading(true);
    const FEE_ID =
      slt_feename.current.selectedOptions[0].getAttribute("fee_id");
    const MODE_ID =
      slt_receipt_mode.current.selectedOptions[0].getAttribute("mode_id");
    const applicable_month = mApplicableMonth.toString();
    const is_special_fee =
      slt_feename.current.selectedOptions[0].getAttribute("is_special_fee");

    const params = new URLSearchParams();
    params.append("session_id", SESSION_ID);
    params.append("class_id", CLASS_ID);
    params.append("fee_id", FEE_ID);
    params.append("fee_value", FEE_VALUE);
    params.append("fee_value_for_existing_student", FEE_VALUE_READMISSION);
    params.append("applicable_month", applicable_month);
    params.append("mode_id", MODE_ID);
    params.append("is_special_fee", is_special_fee);
    params.append("school_code", SessionManager.getSchoolCode());

    axios
      .post(
        `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}AddClassFee.php`,
        params
      )
      .then((res) => {
        setLoading(false);
        const response = res.data;
        if (response.api_status === "OK") {
          myToast.notify_success("Class Fee Added Successfully");
          history.push("/AdminMain/ClassFee");
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  const fieldLabelStyle = {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: COLORS.black7,
    marginBottom: "8px",
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "10px",
    border: `2px solid ${COLORS.primary_color_shade1}`,
    fontSize: "15px",
    fontWeight: 600,
    color: COLORS.black8,
    background: COLORS.white,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
  };

  const submitButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "100%",
    fontSize: "17px",
    fontWeight: 700,
    padding: "14px 20px",
    borderRadius: "10px",
    border: `2px solid ${COLORS.primary_color_shade8}`,
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade5}, ${COLORS.primary_color_shade8})`,
    boxShadow: "0 4px 14px rgba(2, 119, 189, 0.35)",
    color: COLORS.white,
    cursor: "pointer",
    lineHeight: 1.3,
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  const hasClassSelected = Boolean(CLASS_ID && SESSION_ID);

  return (
    <Router>
      <Switch>
        <Route
          path="/AdminMain/AddClassFee/:selected_class_pos"
          exact
          render={() => (
            <div className="content" style={{ background: COLORS.grey1 }}>
              <div className="content-header d-flex align-items-center justify-content-between">
                <div className="leftside-content-header">
                  <ul className="breadcrumbs">
                    <li>
                      <i className="fa fa-home" aria-hidden="true" />
                      <a href="#">Add Class Fee</a>
                    </li>
                  </ul>
                </div>
                <BackButton
                  onClick={() => history.push("/AdminMain/ClassFee")}
                />
              </div>

              <div
                className="bg_page_white_rounded box_shadow"
                style={{ "--mMargin": "25px", padding: "0 0 48px 0" }}
              >
                <div
                  style={{
                    background: CARD_GRADIENT,
                    borderRadius: "12px 12px 0 0",
                    padding: "20px 24px",
                    color: COLORS.white,
                    marginBottom: "20px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "22px",
                        flexShrink: 0,
                      }}
                    >
                      <i className="fa fa-plus-circle" aria-hidden="true" />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontWeight: 700, fontSize: "20px" }}>
                        Add Class Fee
                      </h4>
                      <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: "13px" }}>
                        Configure fee type, receipt mode, amount, and applicable months
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ padding: "0 20px" }}>
                  <SelectClass
                    action={CallBackClassSelect}
                    selected_position={selected_class_pos}
                    session_id={SESSION_ID}
                  />

                  {loading ? (
                    <div style={{ padding: "40px 0" }}>
                      <MyLoader />
                    </div>
                  ) : !hasClassSelected ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "48px 24px",
                        borderRadius: "12px",
                        border: `1px dashed ${COLORS.grey4}`,
                        background: COLORS.grey0,
                        color: COLORS.black6,
                        marginTop: "16px",
                      }}
                    >
                      <i
                        className="fa fa-graduation-cap"
                        style={{
                          fontSize: "36px",
                          color: COLORS.primary_color_shade5,
                          marginBottom: "12px",
                        }}
                      />
                      <p
                        style={{
                          margin: 0,
                          fontSize: "15px",
                          fontWeight: 600,
                          color: COLORS.black7,
                        }}
                      >
                        Select a class to add fees
                      </p>
                      <p style={{ margin: "6px 0 0", fontSize: "13px" }}>
                        Choose session, class, and section above to continue
                      </p>
                    </div>
                  ) : mFeeFiltered.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "48px 24px",
                        borderRadius: "12px",
                        border: `1px dashed ${COLORS.grey4}`,
                        background: COLORS.grey0,
                        color: COLORS.black6,
                        marginTop: "16px",
                      }}
                    >
                      <i
                        className="fa fa-check-circle"
                        style={{
                          fontSize: "36px",
                          color: COLORS.card_green,
                          marginBottom: "12px",
                        }}
                      />
                      <p
                        style={{
                          margin: 0,
                          fontSize: "15px",
                          fontWeight: 600,
                          color: COLORS.black7,
                        }}
                      >
                        All applicable fees are already added
                      </p>
                      <p style={{ margin: "6px 0 0", fontSize: "13px" }}>
                        Return to Class Fee to view or edit existing fees
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        marginTop: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <SectionPanel
                        icon="fa-tag"
                        title="Fee Name & Details"
                        subtitle="Choose fee type, receipt mode, and amount for this class"
                        badge={`${mFeeFiltered.length} available`}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: showInputReAdm
                              ? "repeat(auto-fit, minmax(180px, 1fr))"
                              : "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: "16px",
                          }}
                        >
                          <StyledSelect
                            label="Fee Name"
                            icon="fa-list-alt"
                            selectRef={slt_feename}
                            onChange={onFeeNameChange}
                            hint="Only fees not yet added for this class are listed"
                          >
                            <option>Select Fee</option>
                            {mFeeFiltered.map((item, index) => (
                              <option
                                key={index}
                                fee_id={item.fee_id}
                                position={index}
                                is_special_fee={item.is_special_fee}
                              >
                                {item.fee_name}
                              </option>
                            ))}
                          </StyledSelect>

                          <StyledSelect
                            label="Receipt Mode"
                            icon="fa-calendar-check-o"
                            selectRef={slt_receipt_mode}
                            onChange={onReceiptModeChange}
                            hint="Determines how many months you need to select"
                          >
                            <option>Select Mode</option>
                            {mFeeReceiptMode.map((item, index) => (
                              <option
                                key={index}
                                mode_id={item.mode_id}
                                position={index}
                                value={item.mode}
                              >
                                {item.mode}
                              </option>
                            ))}
                          </StyledSelect>

                          <div>
                            <div style={fieldLabelStyle}>
                              {showInputReAdm
                                ? "Fee Value (New Admission)"
                                : "Fee Value"}
                            </div>
                            <input
                              type="text"
                              value={FEE_VALUE}
                              onChange={onFeeValueChange}
                              style={inputStyle}
                              placeholder="₹ Amount"
                            />
                          </div>

                          {showInputReAdm && (
                            <div>
                              <div style={fieldLabelStyle}>
                                Fee Value (Re-Admission)
                              </div>
                              <input
                                type="text"
                                value={FEE_VALUE_READMISSION}
                                onChange={onFeeValueReAdmChange}
                                style={inputStyle}
                                placeholder="₹ Re-admission amount"
                              />
                            </div>
                          )}
                        </div>
                      </SectionPanel>

                      <SectionPanel
                        icon="fa-calendar"
                        title="Applicable Months"
                        subtitle={monthSelectionHint()}
                        headerCenter={
                          <SelectedFeeHeader
                            fee={selectedFee}
                            modeLabel={selectedModeLabel}
                            feeValue={FEE_VALUE}
                            readmFeeValue={FEE_VALUE_READMISSION}
                            showReadm={showInputReAdm}
                          />
                        }
                        badge={
                          mApplicableMonth.length > 0
                            ? `${mApplicableMonth.length} selected`
                            : "None yet"
                        }
                        accent="green"
                      >
                        {payment_count > 0 && (
                          <div style={{ marginBottom: "14px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "6px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: COLORS.black7,
                                }}
                              >
                                Selection progress
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: monthSelectionComplete()
                                    ? COLORS.leaf_green
                                    : COLORS.primary_color_shade8,
                                }}
                              >
                                {monthSelectionComplete() ? "Ready" : "In progress"}
                              </span>
                            </div>
                            <div
                              style={{
                                height: "8px",
                                borderRadius: "999px",
                                background: COLORS.grey2,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${monthSelectionProgress()}%`,
                                  borderRadius: "999px",
                                  background: monthSelectionComplete()
                                    ? `linear-gradient(90deg, ${COLORS.card_green}, ${COLORS.leaf_green})`
                                    : `linear-gradient(90deg, ${COLORS.primary_color_shade5}, ${COLORS.primary_color_shade7})`,
                                  transition: "width 0.2s ease",
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {mSessionMonth.length === 0 ? (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "32px 20px",
                              borderRadius: "10px",
                              border: `2px dashed ${COLORS.grey3}`,
                              background: COLORS.grey0,
                              color: COLORS.black6,
                            }}
                          >
                            <i
                              className="fa fa-hand-pointer-o"
                              style={{
                                fontSize: "28px",
                                color: COLORS.primary_color_shade5,
                                marginBottom: "10px",
                              }}
                            />
                            <p
                              style={{
                                margin: 0,
                                fontSize: "14px",
                                fontWeight: 600,
                                color: COLORS.black7,
                              }}
                            >
                              Select a receipt mode above
                            </p>
                            <p style={{ margin: "6px 0 0", fontSize: "12px" }}>
                              Session months will appear here for selection
                            </p>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                              gap: "8px",
                            }}
                          >
                            {mSessionMonth.map((item, index) => (
                              <MonthPickerChip
                                key={index}
                                monthKey={item}
                                selected={isMonthApplicable(item)}
                                onToggle={updateApplicableMonth}
                              />
                            ))}
                          </div>
                        )}

                        {mApplicableMonth.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "6px",
                              marginTop: "14px",
                              paddingTop: "14px",
                              borderTop: `1px solid ${COLORS.grey2}`,
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 700,
                                color: COLORS.black6,
                                alignSelf: "center",
                                marginRight: "4px",
                              }}
                            >
                              Selected:
                            </span>
                            {[...mApplicableMonth]
                              .sort()
                              .map((month) => {
                                const { monthLabel, shortKey } = parseMonthKey(month);
                                return (
                                  <span
                                    key={month}
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 700,
                                      padding: "4px 10px",
                                      borderRadius: "999px",
                                      background: COLORS.green_light,
                                      color: COLORS.leaf_green,
                                      border: `1px solid ${COLORS.green_dark}`,
                                    }}
                                  >
                                    {monthLabel} ({shortKey})
                                  </span>
                                );
                              })}
                          </div>
                        )}
                      </SectionPanel>
                    </div>
                  )}

                  {hasClassSelected && mFeeFiltered.length > 0 && !loading && (
                    <div style={{ marginTop: "24px" }}>
                      <button
                        type="button"
                        onClick={submitClassFee}
                        style={submitButtonStyle}
                      >
                        <i className="fa fa-check-circle" aria-hidden="true" />
                        Submit Class Fee
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        />
        <Route path="/AdminMain/ClassFee" component={ClassFee} />
      </Switch>
    </Router>
  );
};

export default AddClassFee;
